import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useDefaultClient } from "~/hooks/useDefaultClient"
import { useSettings } from "~/hooks/useSettings"
import { useVNZAuthenticationState } from "./useVNZAuthenticationState"
import { useVNZService } from "./useVNZService"

export const VNZ_OAUTH_CHANNEL_NAME = "VNZ_oauth_channel"
const AUTH_TIMEOUT_MS = 60_000 * 1 // 1 minute

let authTimeoutId: ReturnType<typeof setTimeout> | null = null

export const useVNZAuthentication = () => {
  const { t } = useTranslation()
  const { client } = useDefaultClient()
  const verizonService = useVNZService()

  const {
    settings: { deviceToken },
  } = useSettings()

  const verizonAuthUrl = verizonService.getAuthenticationUrl(
    client.id,
    deviceToken
  )

  const {
    setIsAuthenticating,
    setIsAuthenticated,
    setAuthenticationError,
    state: { authenticationError, hasAuthenticationError },
  } = useVNZAuthenticationState()

  const startAuthentication = () => {
    if (authTimeoutId) {
      clearTimeout(authTimeoutId)
    }

    authTimeoutId = setTimeout(() => {
      setIsAuthenticating({ isAuthenticating: false })
      setAuthenticationError({
        authenticationError: t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.AuthenticationTimeoutError"
        ),
      })
      authTimeoutId = null
    }, AUTH_TIMEOUT_MS)

    setIsAuthenticating({ isAuthenticating: true })

    const width = 520
    const height = 520
    const left = Math.round(window.screenX + (window.outerWidth - width) / 2)
    const top = Math.round(window.screenY + (window.outerHeight - height) / 2)

    const popup = window.open(
      verizonAuthUrl,
      "_blank",
      `width=${width},height=${height},left=${left},top=${top}`
    )

    if (!popup) {
      setAuthenticationError({
        authenticationError: t(
          "components.cloudProvider.vnz.browser.VNZImageBrowser.PopupBlockedError"
        ),
      })
    }
  }

  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(VNZ_OAUTH_CHANNEL_NAME)

    broadcastChannel.onmessage = async event => {
      try {
        const { code, state: returnedState } = event.data

        const decodedState = JSON.parse(decodeURIComponent(returnedState))

        if (
          !decodedState.deviceToken ||
          decodedState.deviceToken !== deviceToken
        ) {
          const errorMessage = t(
            "components.cloudProvider.vnz.browser.VNZImageBrowser.InvalidStateError"
          )
          console.error(errorMessage)
          throw new Error(errorMessage)
        }

        const tokenData = await verizonService.getAccessToken(code)

        if (authTimeoutId) {
          clearTimeout(authTimeoutId)
          authTimeoutId = null
        }

        setIsAuthenticated({
          accessToken: tokenData.accessToken,
          lastRefreshDate: new Date().toISOString(),
          refreshToken: tokenData.refreshToken,
          tokenValidityInSeconds: tokenData.expiresIn,
        })
      } catch (error) {
        console.log(error)
        setAuthenticationError({
          authenticationError: "Failed to request access token.",
        })
      }
    }
    return () => {
      broadcastChannel.close()
    }
  }, [
    t,
    verizonService,
    deviceToken,
    setAuthenticationError,
    setIsAuthenticated,
  ])

  return {
    startAuthentication,
    error: authenticationError,
    hasError: hasAuthenticationError,
  }
}
